import React, { useEffect, useRef, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, TextField, CircularProgress, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Radio, Checkbox,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SignatureCanvas from 'react-signature-canvas';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import ProjectService from '../services/ProjectService';
import CustomerService from '../services/CustomerService';
import UserService from '../services/UserService';
import SecureImage from './SecureImage';
import axiosInstance from '../services/axiosInstance';
import { useToast } from '../contexts/ToastContext';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

// ─── Phase 1: Employee review ─────────────────────────────────────────────────

function EmployeePhase({ report, images, onHandOver, onMarkNotAvailable }) {
  const total = (report.selectedEntryIds?.length || 0)
    + (report.selectedExpenseIds?.length || 0)
    + (report.selectedSubServiceIds?.length || 0)
    + (report.selectedInventoryIds?.length || 0);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC', p: 3 }}>
      <Box sx={{ maxWidth: 480, width: '100%' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 3, textAlign: 'center' }}>
          Employee Review
        </Typography>

        <Box sx={{ bgcolor: '#fff', border: '1px solid #EEF2F6', borderRadius: 2.5, p: 3, mb: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
          <Typography sx={{ fontSize: 19, fontWeight: 700, color: '#1E293B', mb: 0.5 }}>
            {report.projectName || '—'}
          </Typography>
          {report.customerName && (
            <Typography sx={{ fontSize: 13, color: '#64748B', mb: 0.5 }}>Customer: {report.customerName}</Typography>
          )}
          <Typography sx={{ fontSize: 13, color: '#64748B', mb: 2 }}>
            {report.displayName} · {formatDate(report.reportDate)}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', gap: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#427898' }}>{total}</Typography>
              <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>Items</Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography sx={{ fontSize: 22, fontWeight: 700, color: '#427898' }}>{images.length}</Typography>
              <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>Photos</Typography>
            </Box>
          </Box>
          {report.workDescription && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #EEF2F6' }}>
              <Typography sx={{ fontSize: 13, color: '#64748B', whiteSpace: 'pre-wrap' }}>{report.workDescription}</Typography>
            </Box>
          )}
        </Box>

        <Button
          fullWidth variant="contained" onClick={onHandOver}
          sx={{ py: 1.5, fontSize: 15, fontWeight: 700, bgcolor: '#427898', borderRadius: 2, textTransform: 'none', mb: 2, '&:hover': { bgcolor: '#355f79' } }}
        >
          Sign Report →
        </Button>
        <Button
          fullWidth variant="text" onClick={onMarkNotAvailable}
          sx={{ fontSize: 13, color: '#94A3B8', textTransform: 'none' }}
        >
          Customer not available
        </Button>
      </Box>
    </Box>
  );
}

// ─── Phase 2: Employee signature ─────────────────────────────────────────────

function EmployeeSignPhase({ onSigned, onBack, defaultName = '' }) {
  const sigCanvas = useRef(null);
  const [employeeName, setEmployeeName] = useState(defaultName);
  const [hasStrokes, setHasStrokes] = useState(false);
  const canSubmit = employeeName.trim().length > 0 && hasStrokes;

  function handleConfirm() {
    if (!canSubmit) return;
    onSigned({
      employeeSignatureName: employeeName.trim(),
      employeeSignatureData: sigCanvas.current.toDataURL('image/png'),
    });
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC', p: 3 }}>
      <Box sx={{ maxWidth: 480, width: '100%' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 1, textAlign: 'center' }}>
          Employee Confirmation
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#64748B', textAlign: 'center', mb: 3 }}>
          Sign below to confirm you performed the work described in this report.
        </Typography>

        <Box sx={{ bgcolor: '#fff', border: '1px solid #EEF2F6', borderRadius: 2.5, p: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
          <TextField
            fullWidth size="small" label="Your name"
            value={employeeName}
            InputProps={{ readOnly: true }}
            sx={{ mb: 3, '& .MuiInputBase-input': { cursor: 'default' } }}
          />

          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>Signature *</Typography>
              <Button size="small" onClick={() => { sigCanvas.current?.clear(); setHasStrokes(false); }}
                sx={{ fontSize: 12, color: '#94A3B8', textTransform: 'none' }}>
                Clear
              </Button>
            </Box>
            <Box sx={{ border: '2px solid #CBD5E1', borderRadius: 2, overflow: 'hidden', bgcolor: '#FAFBFC', position: 'relative' }}>
              {!hasStrokes && (
                <Typography sx={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
                  fontSize: 14, color: '#CBD5E1', pointerEvents: 'none', userSelect: 'none',
                }}>
                  Sign here
                </Typography>
              )}
              <SignatureCanvas
                ref={sigCanvas} penColor="#1E293B"
                canvasProps={{ style: { width: '100%', height: 140, display: 'block' } }}
                onEnd={() => setHasStrokes(true)}
              />
            </Box>
          </Box>
        </Box>

        <Button
          fullWidth variant="contained" disabled={!canSubmit} onClick={handleConfirm}
          sx={{ mt: 3, py: 1.5, fontSize: 15, fontWeight: 700, bgcolor: '#427898', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#355f79' }, '&.Mui-disabled': { bgcolor: '#CBD5E1', color: '#94A3B8' } }}
        >
          Confirm & Hand to Customer →
        </Button>
        <Button fullWidth variant="text" onClick={onBack}
          sx={{ mt: 1, fontSize: 13, color: '#94A3B8', textTransform: 'none' }}>
          ← Back to Review
        </Button>
      </Box>
    </Box>
  );
}

// ─── Phase 3: Customer contact selection ─────────────────────────────────────

function ContactSelectStep({ customerId, onSelected, onBack }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const { showToast } = useToast();

  useEffect(() => {
    CustomerService.getContactsByCustomer(customerId)
      .then(list => setContacts(list || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [customerId]);

  function validateNew() {
    const e = {};
    if (!newForm.firstName.trim()) e.firstName = 'Required';
    if (!newForm.lastName.trim()) e.lastName = 'Required';
    if (!newForm.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newForm.email)) e.email = 'Invalid email';
    if (!newForm.phone.trim()) e.phone = 'Required';
    return e;
  }

  async function handleAddContact() {
    const e = validateNew();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const created = await CustomerService.createContact({
        customer: customerId,
        firstName: newForm.firstName.trim(),
        lastName: newForm.lastName.trim(),
        email: newForm.email.trim(),
        phone: newForm.phone.trim(),
      });
      setContacts(prev => [...prev, created]);
      setSelectedId(created.id);
      setShowNewForm(false);
      setNewForm({ firstName: '', lastName: '', email: '', phone: '' });
      setErrors({});
    } catch {
      showToast('Failed to create contact', 'error');
    } finally {
      setSaving(false);
    }
  }

  const selected = contacts.find(c => c.id === selectedId);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC', p: 3 }}>
      <Box sx={{ maxWidth: 480, width: '100%' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1, mb: 1, textAlign: 'center' }}>
          Who is signing?
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#64748B', textAlign: 'center', mb: 3 }}>
          Select an existing contact or add a new one.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: '#427898' }} />
          </Box>
        ) : (
          <Box sx={{ bgcolor: '#fff', border: '1px solid #EEF2F6', borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)', mb: 3 }}>
            {contacts.map((c, idx) => (
              <Box
                key={c.id}
                onClick={() => setSelectedId(c.id)}
                sx={{
                  display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer',
                  borderBottom: idx < contacts.length - 1 ? '1px solid #EEF2F6' : 'none',
                  bgcolor: selectedId === c.id ? '#EFF6FF' : 'transparent',
                  '&:hover': { bgcolor: selectedId === c.id ? '#EFF6FF' : '#F8FAFC' },
                }}
              >
                <Radio
                  checked={selectedId === c.id}
                  onChange={() => setSelectedId(c.id)}
                  size="small"
                  sx={{ mr: 1.5, p: 0, color: '#CBD5E1', '&.Mui-checked': { color: '#427898' } }}
                />
                <PersonIcon sx={{ fontSize: 16, color: '#94A3B8', mr: 1 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#1E293B' }}>
                    {c.firstName} {c.lastName}
                  </Typography>
                  {(c.email || c.phone) && (
                    <Typography sx={{ fontSize: 12, color: '#64748B' }}>
                      {[c.email, c.phone].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}

            {!showNewForm ? (
              <Box
                onClick={() => setShowNewForm(true)}
                sx={{
                  display: 'flex', alignItems: 'center', px: 2, py: 1.5, cursor: 'pointer',
                  color: '#427898', borderTop: contacts.length ? '1px solid #EEF2F6' : 'none',
                  '&:hover': { bgcolor: '#F8FAFC' },
                }}
              >
                <AddIcon sx={{ fontSize: 16, mr: 1 }} />
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>Add new contact</Typography>
              </Box>
            ) : (
              <Box sx={{ p: 2, borderTop: contacts.length ? '1px solid #EEF2F6' : 'none', bgcolor: '#FAFBFE' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#427898', mb: 1.5, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  New contact
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
                  <TextField
                    size="small" label="First name *" value={newForm.firstName}
                    onChange={e => setNewForm(p => ({ ...p, firstName: e.target.value }))}
                    error={!!errors.firstName} helperText={errors.firstName}
                  />
                  <TextField
                    size="small" label="Last name *" value={newForm.lastName}
                    onChange={e => setNewForm(p => ({ ...p, lastName: e.target.value }))}
                    error={!!errors.lastName} helperText={errors.lastName}
                  />
                </Box>
                <TextField
                  fullWidth size="small" label="Email *" value={newForm.email}
                  onChange={e => setNewForm(p => ({ ...p, email: e.target.value }))}
                  error={!!errors.email} helperText={errors.email}
                  sx={{ mb: 1.5 }}
                />
                <TextField
                  fullWidth size="small" label="Phone *" value={newForm.phone}
                  onChange={e => setNewForm(p => ({ ...p, phone: e.target.value }))}
                  error={!!errors.phone} helperText={errors.phone}
                  sx={{ mb: 1.5 }}
                />
                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={() => { setShowNewForm(false); setErrors({}); setNewForm({ firstName: '', lastName: '', email: '', phone: '' }); }}
                    sx={{ textTransform: 'none', color: '#64748B', fontSize: 13 }}>
                    Cancel
                  </Button>
                  <Button size="small" variant="contained" onClick={handleAddContact} disabled={saving}
                    sx={{ textTransform: 'none', bgcolor: '#427898', fontSize: 13, '&:hover': { bgcolor: '#355f79' } }}>
                    {saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : 'Save'}
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        )}

        <Button
          fullWidth variant="contained" disabled={!selectedId} onClick={() => onSelected(selected)}
          sx={{ py: 1.5, fontSize: 15, fontWeight: 700, bgcolor: '#427898', borderRadius: 2, textTransform: 'none', mb: 1, '&:hover': { bgcolor: '#355f79' }, '&.Mui-disabled': { bgcolor: '#CBD5E1', color: '#94A3B8' } }}
        >
          Continue →
        </Button>
        <Button fullWidth variant="text" onClick={onBack}
          sx={{ fontSize: 13, color: '#94A3B8', textTransform: 'none' }}>
          ← Back
        </Button>
      </Box>
    </Box>
  );
}

// ─── Phase 3: Customer signing ────────────────────────────────────────────────

function CustomerPhase({ report, images, onSigned, onRefuse, onBack }) {
  const [subStep, setSubStep] = useState('select'); // 'select' | 'sign'
  const [signerContact, setSignerContact] = useState(null);

  if (subStep === 'select') {
    return (
      <ContactSelectStep
        customerId={report.customerId}
        onSelected={contact => { setSignerContact(contact); setSubStep('sign'); }}
        onBack={onBack}
      />
    );
  }

  return (
    <CustomerSignStep
      report={report}
      images={images}
      signerContact={signerContact}
      onBack={() => setSubStep('select')}
      onSigned={onSigned}
      onRefuse={onRefuse}
    />
  );
}

function CustomerSignStep({ report, images, signerContact, onBack, onSigned, onRefuse }) {
  const sigCanvas = useRef(null);
  const [signerName, setSignerName] = useState(
    signerContact ? `${signerContact.firstName} ${signerContact.lastName}` : ''
  );
  const [customerRemarks, setCustomerRemarks] = useState('');
  const [isSigning, setIsSigning] = useState(false);
  const [hasStrokes, setHasStrokes] = useState(false);
  const [refuseDialogOpen, setRefuseDialogOpen] = useState(false);
  const [refuseReason, setRefuseReason] = useState('');
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let blobUrl = null;
    axiosInstance.get(`/api/reports/${report.id}/pdf`)
      .then(res => {
        const bytes = atob(res.data);
        const arr = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
        blobUrl = URL.createObjectURL(new Blob([arr], { type: 'application/pdf' }));
        if (!cancelled) setPdfUrl(blobUrl);
        else URL.revokeObjectURL(blobUrl);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setPdfLoading(false); });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [report.id]);

  const canSubmit = signerName.trim().length > 0 && hasStrokes;

  async function handleConfirm() {
    if (!canSubmit) return;
    setIsSigning(true);
    try {
      await onSigned({
        signerName: signerName.trim(),
        signatureData: sigCanvas.current.toDataURL('image/png'),
        customerRemarks,
        signerContactId: signerContact?.id ?? null,
      });
    } finally {
      setIsSigning(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff', pb: 6 }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 3, pb: 2, borderBottom: '1px solid #EEF2F6', textAlign: 'center', bgcolor: '#F8FAFC' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 1 }}>
          Service Report
        </Typography>
        <Typography sx={{ fontSize: 17, fontWeight: 700, color: '#1E293B', mt: 0.5 }}>
          {report.projectName || report.displayName}
        </Typography>
        {report.customerName && (
          <Typography sx={{ fontSize: 13, color: '#64748B', mt: 0.25 }}>for {report.customerName}</Typography>
        )}
      </Box>

      <Box sx={{ maxWidth: 720, mx: 'auto', px: 2, pt: 3 }}>
        {/* PDF preview */}
        <Box sx={{ mb: 3, border: '1px solid #EEF2F6', borderRadius: 2, overflow: 'hidden', bgcolor: '#F8FAFC', minHeight: 480 }}>
          {pdfLoading ? (
            <Box sx={{ height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress sx={{ color: '#427898' }} />
            </Box>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="Report"
              style={{ width: '100%', height: 520, border: 'none', display: 'block' }}
            />
          ) : (
            <Box sx={{ height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: 13, color: '#94A3B8' }}>PDF preview not available</Typography>
            </Box>
          )}
        </Box>

        {/* Photos */}
        {images.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
              Photos ({images.length})
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1 }}>
              {images.map(img => (
                <Box key={img.id} sx={{ borderRadius: 1.5, overflow: 'hidden', border: '1px solid #EEF2F6' }}>
                  <SecureImage
                    objectKey={img.objectKey}
                    alt={img.caption || ''}
                    style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }}
                  />
                  {img.caption && (
                    <Typography sx={{ fontSize: 11, color: '#64748B', px: 1, py: 0.5 }}>{img.caption}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B', mb: 2 }}>
          Please review the report above and sign below to confirm.
        </Typography>

        {/* Signer name */}
        <TextField
          fullWidth size="small" label="Customer name"
          value={signerName}
          InputProps={{ readOnly: true }}
          sx={{ mb: 2, '& .MuiInputBase-input': { cursor: 'default' } }}
        />

        {/* Remarks */}
        <TextField
          fullWidth size="small" multiline rows={2} label="Remarks (optional)"
          value={customerRemarks} onChange={e => setCustomerRemarks(e.target.value)}
          sx={{ mb: 3 }}
        />

        {/* Signature canvas */}
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>Signature *</Typography>
            <Button size="small" onClick={() => { sigCanvas.current?.clear(); setHasStrokes(false); }}
              sx={{ fontSize: 12, color: '#94A3B8', textTransform: 'none' }}>
              Clear
            </Button>
          </Box>
          <Box sx={{ border: '2px solid #CBD5E1', borderRadius: 2, overflow: 'hidden', bgcolor: '#FAFBFC', position: 'relative' }}>
            {!hasStrokes && (
              <Typography sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                fontSize: 14, color: '#CBD5E1', pointerEvents: 'none', userSelect: 'none',
              }}>
                Sign here
              </Typography>
            )}
            <SignatureCanvas
              ref={sigCanvas} penColor="#1E293B"
              canvasProps={{ style: { width: '100%', height: 160, display: 'block' } }}
              onEnd={() => setHasStrokes(true)}
            />
          </Box>
        </Box>

        {/* Confirm button */}
        <Button
          fullWidth variant="contained" disabled={!canSubmit || isSigning} onClick={handleConfirm}
          sx={{
            mt: 3, py: 1.5, fontSize: 15, fontWeight: 700, borderRadius: 2,
            textTransform: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#355f79' },
            '&.Mui-disabled': { bgcolor: '#CBD5E1', color: '#94A3B8' },
          }}
        >
          {isSigning ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Confirm & Sign'}
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
          <Button size="small" onClick={onBack}
            sx={{ fontSize: 12, color: '#94A3B8', textTransform: 'none' }}>
            ← Change contact
          </Button>
          <Button size="small" onClick={() => setRefuseDialogOpen(true)}
            sx={{ fontSize: 12, color: '#94A3B8', textTransform: 'none' }}>
            Customer refuses to sign
          </Button>
        </Box>
      </Box>

      {/* Refuse dialog */}
      <Dialog open={refuseDialogOpen} onClose={() => setRefuseDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Reason for refusal</Typography>
          <IconButton size="small" onClick={() => setRefuseDialogOpen(false)}><CloseIcon sx={{ fontSize: 17 }} /></IconButton>
        </DialogTitle>
        <DialogContent>
          <TextField fullWidth multiline rows={3} size="small" label="Reason (optional)"
            value={refuseReason} onChange={e => setRefuseReason(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRefuseDialogOpen(false)} sx={{ textTransform: 'none', color: '#64748B' }}>Cancel</Button>
          <Button variant="contained" onClick={() => { setRefuseDialogOpen(false); onRefuse(refuseReason); }}
            sx={{ textTransform: 'none', bgcolor: '#EF4444', '&:hover': { bgcolor: '#DC2626' } }}>
            Confirm Refusal
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Phase: Done ──────────────────────────────────────────────────────────────

function DonePhase({ outcome, returnTo, reportId, report: initialReport }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [emailBody, setEmailBody] = useState('');
  const [sending, setSending] = useState(false);
  const [emailSentAt, setEmailSentAt] = useState(initialReport?.emailSentAt || null);
  const [emailSentByName, setEmailSentByName] = useState(initialReport?.emailSentByName || null);

  function openEmailDialog() {
    setEmailDialog(true);
    if (initialReport?.customerId && contacts.length === 0) {
      setContactsLoading(true);
      CustomerService.getContactsByCustomer(initialReport.customerId)
        .then(list => {
          const l = list || [];
          setContacts(l);
          setSelectedIds(l.filter(c => c.email).map(c => c.id));
        })
        .catch(() => {})
        .finally(() => setContactsLoading(false));
    }
    if (!emailBody) {
      setEmailBody(`Dear Customer,\n\nPlease find attached the service report${initialReport?.displayName ? ` "${initialReport.displayName}"` : ''} for your records.\n\nBest regards`);
    }
  }

  async function handleSendEmail() {
    const to = contacts.filter(c => selectedIds.includes(c.id)).map(c => c.email).filter(Boolean);
    if (!to.length) { showToast('Select at least one contact with an email address', 'warning'); return; }
    setSending(true);
    try {
      await ProjectService.sendReportEmail(reportId, { to, body: emailBody });
      const updated = await ProjectService.getReport(reportId);
      setEmailSentAt(updated.emailSentAt);
      setEmailSentByName(updated.emailSentByName);
      setEmailDialog(false);
      showToast('Report sent by email', 'success');
    } catch {
      showToast('Failed to send email', 'error');
    } finally {
      setSending(false);
    }
  }

  const meta = {
    signed:        { color: '#22c55e', icon: <CheckCircleOutlineIcon sx={{ fontSize: 56, color: '#22c55e', mb: 1 }} />, title: 'Report Signed', sub: 'Thank you. The report has been signed successfully.' },
    refused:       { color: '#ef4444', icon: null, title: 'Refusal Recorded',       sub: 'The refusal has been noted.' },
    not_available: { color: '#f59e0b', icon: null, title: 'Marked as Not Available', sub: 'The report has been updated.' },
  }[outcome] || { color: '#427898', icon: null, title: 'Done', sub: '' };

  async function handleDownloadPdf() {
    setPdfLoading(true);
    try {
      const response = await ProjectService.downloadPdfReport(reportId);
      const blob = response instanceof Blob ? response : response.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-${reportId}.pdf`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      showToast('PDF is still being generated, please try again shortly.', 'warning');
    } finally {
      setPdfLoading(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#F8FAFC', gap: 1, p: 3 }}>
      {meta.icon}
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: meta.color }}>{meta.title}</Typography>
      {meta.sub && <Typography sx={{ fontSize: 14, color: '#64748B', textAlign: 'center', maxWidth: 320 }}>{meta.sub}</Typography>}

      {emailSentAt && (
        <Box sx={{ mt: 1.5, px: 2.5, py: 1, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon sx={{ fontSize: 15, color: '#16A34A' }} />
          <Typography sx={{ fontSize: 12, color: '#16A34A' }}>
            Emailed on {new Date(emailSentAt).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            {emailSentByName ? ` by ${emailSentByName}` : ''}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, mt: 3, flexWrap: 'wrap', justifyContent: 'center' }}>
        {outcome === 'signed' && (
          <>
            <Button
              variant="outlined"
              onClick={handleDownloadPdf}
              disabled={pdfLoading}
              sx={{ textTransform: 'none', borderColor: '#427898', color: '#427898' }}
            >
              {pdfLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Download PDF
            </Button>
            <Button
              variant="outlined"
              startIcon={<EmailIcon />}
              onClick={openEmailDialog}
              sx={{ textTransform: 'none', borderColor: '#16A34A', color: '#16A34A' }}
            >
              Send by Email
            </Button>
          </>
        )}
        <Button
          variant="outlined"
          onClick={() => returnTo ? navigate(returnTo) : window.close()}
          sx={{ textTransform: 'none', borderColor: '#CBD5E1', color: '#64748B' }}
        >
          {returnTo ? 'Back to Field Reports' : 'Close'}
        </Button>
      </Box>

      {/* Email dialog */}
      <Dialog open={emailDialog} onClose={() => !sending && setEmailDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Send Report by Email</Typography>
          <IconButton size="small" onClick={() => setEmailDialog(false)} disabled={sending}>
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1 }}>
            Recipients
          </Typography>
          {contactsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} sx={{ color: '#427898' }} />
            </Box>
          ) : contacts.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: '#94A3B8', mb: 2 }}>No contacts found for this customer.</Typography>
          ) : (
            <Box sx={{ bgcolor: '#F8FAFC', borderRadius: 1.5, border: '1px solid #EEF2F6', mb: 2 }}>
              {contacts.map((c, idx) => (
                <Box key={c.id} sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25, borderBottom: idx < contacts.length - 1 ? '1px solid #EEF2F6' : 'none' }}>
                  <Checkbox
                    size="small"
                    checked={selectedIds.includes(c.id)}
                    onChange={e => setSelectedIds(prev => e.target.checked ? [...prev, c.id] : prev.filter(x => x !== c.id))}
                    disabled={!c.email}
                    sx={{ p: 0, mr: 1.5, color: '#CBD5E1', '&.Mui-checked': { color: '#427898' } }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B' }}>
                      {c.firstName} {c.lastName}
                    </Typography>
                    {c.email ? (
                      <Typography sx={{ fontSize: 12, color: '#64748B' }}>{c.email}</Typography>
                    ) : (
                      <Typography sx={{ fontSize: 12, color: '#CBD5E1' }}>No email address</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          <TextField
            fullWidth multiline rows={3} size="small"
            label="Message (optional)"
            value={emailBody}
            onChange={e => setEmailBody(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEmailDialog(false)} disabled={sending}
            sx={{ textTransform: 'none', color: '#64748B' }}>
            Cancel
          </Button>
          <Button
            variant="contained" onClick={handleSendEmail} disabled={sending}
            sx={{ textTransform: 'none', bgcolor: '#427898', '&:hover': { bgcolor: '#355f79' } }}
          >
            {sending ? <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} /> : null}
            Send Email
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function SignReportPage() {
  const { id } = useParams();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = location.state?.from || null;

  const [report, setReport] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('employee'); // 'employee' | 'employee_sign' | 'customer' | 'done'
  const [doneOutcome, setDoneOutcome] = useState('signed');
  const [employeeSignatureDto, setEmployeeSignatureDto] = useState(null);
  const [currentUserName, setCurrentUserName] = useState('');

  useEffect(() => {
    const handler = e => {
      e.preventDefault();
      e.returnValue = 'Are you sure you want to leave the signing screen?';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, []);

  useEffect(() => {
    Promise.all([
      ProjectService.getReport(id),
      ProjectService.getReportImages(id),
      UserService.getUserInfo().catch(() => null),
    ]).then(([r, imgs, user]) => {
      setReport(r);
      setImages(imgs || []);
      if (user?.firstName || user?.lastName) {
        setCurrentUserName(`${user.firstName || ''} ${user.lastName || ''}`.trim());
      }
    }).catch(() => {
      showToast('Failed to load report', 'error');
    }).finally(() => setLoading(false));
  }, [id]); // eslint-disable-line

  async function handleSigned(dto) {
    try {
      await ProjectService.signReport(id, { ...dto, ...employeeSignatureDto });
      const updated = await ProjectService.getReport(id).catch(() => null);
      if (updated) setReport(updated);
      setDoneOutcome('signed');
      setPhase('done');
    } catch {
      showToast('Failed to sign report', 'error');
    }
  }

  async function handleRefuse(reason) {
    try {
      await ProjectService.refuseReport(id, { reason });
      setDoneOutcome('refused');
      setPhase('done');
    } catch {
      showToast('Failed to record refusal', 'error');
    }
  }

  async function handleMarkNotAvailable() {
    try {
      await ProjectService.markReportCustomerNotAvailable(id);
      setDoneOutcome('not_available');
      setPhase('done');
    } catch {
      showToast('Failed to update status', 'error');
    }
  }

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#427898' }} />
      </Box>
    );
  }

  if (phase === 'done') return <DonePhase outcome={doneOutcome} returnTo={returnTo} reportId={id} report={report} />;

  if (!report) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ color: '#EF4444' }}>Report not found</Typography>
      </Box>
    );
  }

  if (phase === 'employee') {
    return (
      <EmployeePhase
        report={report} images={images}
        onHandOver={() => setPhase('employee_sign')}
        onMarkNotAvailable={handleMarkNotAvailable}
      />
    );
  }

  if (phase === 'employee_sign') {
    return (
      <EmployeeSignPhase
        defaultName={currentUserName}
        onSigned={dto => { setEmployeeSignatureDto(dto); setPhase('customer'); }}
        onBack={() => setPhase('employee')}
      />
    );
  }

  return (
    <CustomerPhase
      report={report} images={images}
      onSigned={handleSigned}
      onRefuse={handleRefuse}
      onBack={() => setPhase('employee_sign')}
    />
  );
}
